import {Component, OnInit} from '@angular/core';
import {environment} from "../../environments/environment";
import {AuthService} from "../_services/auth.service";
import {TokenStorageService} from "../_services/token-storage.service";
import {BreadcrumbService} from "xng-breadcrumb";

declare const google: any;

@Component({
  standalone: false,
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  form: any = {};
  url = '';
  errorMessage = '';
  roles: string[];
  isLoggedIn = false;
  isLoginFailed = false;

  constructor(private loginRequestService: AuthService,
              private tokenStorage: TokenStorageService,
              private breadcrumbService: BreadcrumbService) {
    environment.then(environment => {
      this.url = environment["backendURL"];
    });
  }

  ngOnInit(): void {
    this.breadcrumbService.set('@Login', 'Login');
    this.syncLoggedInState();

    // Wait for Google script to load
    if (typeof google !== 'undefined') {
      this.initializeGoogleSignIn();
    } else {
      window.addEventListener('load', () => {
        this.initializeGoogleSignIn();
      });
    }
  }

  /** Drop half-written session data so the form can be used. */
  private syncLoggedInState(): void {
    const token = this.tokenStorage.getToken();
    const user = this.tokenStorage.getUser();
    const userOk = this.isUsableUser(user);
    if (token && userOk) {
      this.isLoggedIn = true;
      this.roles = user.roles ?? user.username;
    } else if (token || user) {
      this.tokenStorage.signOut();
      this.isLoggedIn = false;
      this.roles = undefined;
    } else {
      this.isLoggedIn = false;
    }
  }

  private isUsableUser(user: any): boolean {
    if (user == null || typeof user !== 'object') {
      return false;
    }
    const u = user.username;
    const e = user.email;
    const id = user.id;
    return (typeof u === 'string' && u.length > 0)
      || (typeof e === 'string' && e.length > 0)
      || id != null;
  }

  /** Clear stored auth from this page (e.g. switch account). */
  clearLocalSession(): void {
    this.tokenStorage.signOut();
    this.syncLoggedInState();
    this.isLoginFailed = false;
    this.errorMessage = '';
  }

  initializeGoogleSignIn(): void {
    if (typeof google !== 'undefined' && google.accounts) {
      google.accounts.id.initialize({
        client_id: '681071664856-e15pa1gbvof8t1acc4mkq9909lc0b89u.apps.googleusercontent.com',
        callback: (response: any) => this.handleGoogleSignIn(response)
      });
    }
  }

  signInWithGoogle(): void {
    if (typeof google !== 'undefined' && google.accounts) {
      google.accounts.id.prompt();
    }
  }

  handleGoogleSignIn(response: any): void {
    this.isLoginFailed = false;
    this.withBackendUrl((base) => {
      this.loginRequestService.googleLogin(base, { idToken: response.credential }).subscribe(
        data => {
          this.persistAuthResponse(data);
          window.location.href = `${window.location.origin}/profile`;
        },
        err => {
          this.errorMessage = 'Google login failed: ' + (err.error?.message || err.message);
          this.isLoginFailed = true;
        }
      );
    });
  }

  onSubmit(): void {
    this.isLoginFailed = false;
    this.withBackendUrl((base) => {
      this.loginRequestService.login(base, this.form).subscribe(
        data => {
          this.persistAuthResponse(data);
          window.location.href = `${window.location.origin}/profile`;
        },
        err => {
          const body = err.error;
          this.errorMessage =
            (typeof body === 'object' && body?.message) ? body.message
              : (typeof body === 'string' ? body : null)
                ?? err.message
                ?? 'Login failed';
          this.isLoginFailed = true;
        });
    });
  }

  /** Backend JwtResponse uses `token`; some clients use `accessToken`. */
  private persistAuthResponse(data: any): void {
    const jwt = data?.accessToken ?? data?.token;
    if (!jwt) {
      this.errorMessage = 'Login succeeded but no token was returned.';
      this.isLoginFailed = true;
      return;
    }
    this.tokenStorage.saveToken(jwt);
    this.tokenStorage.saveUser(data);
  }

  private withBackendUrl(action: (baseUrl: string) => void): void {
    if (this.url) {
      action(this.url);
      return;
    }
    environment.then((env) => {
      this.url = env['backendURL'];
      if (!this.url) {
        this.errorMessage = 'Server address is not configured.';
        this.isLoginFailed = true;
        return;
      }
      action(this.url);
    });
  }
}
